/**
 * PagePanelContext
 *
 * Two independent slots that let the active page PUBLISH contextual data to the
 * v3 chrome panels:
 *
 * - PageFiltersContext  → consumed by LeftNav (status/severity/custom facets,
 *                         view mode, inline filter/search)
 * - AssistantContext    → consumed by RightTray (current entity + page route,
 *                         suggested prompts) so the AI Assistant always knows
 *                         what the user is looking at.
 *
 * Pages never consume these contexts directly — they call the publisher hooks
 * (usePageFilters / useAssistantContext). LeftNav and RightTray use the
 * subscriber hooks exported from this module.
 *
 * Visibility of the chrome panels stays in PanelContext; this module is only
 * about *content*.
 *
 * Spec: docs/superpowers/specs/2026-03-17-layout-information-density-design.md §1.2, §1.6, §1.9
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import type { FacetItem } from '../components/navigation/FacetPills';
import type { ViewMode } from '../components/navigation/ViewModeSelector';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PageFacetGroup {
  /** Unique key within a page (e.g. "status", "severity", "role"). */
  key: string;
  /** Uppercase section label rendered in LeftNav (e.g. "STATUS"). */
  label: string;
  /** Pills to render. */
  items: FacetItem[];
  /** Currently selected keys; `['all']` or `[]` means "all". */
  selected: string[];
  /** Called when the user toggles pills. */
  onChange: (selected: string[]) => void;
}

export interface PageViewModeInput {
  value: ViewMode;
  onChange: (mode: ViewMode) => void;
  /** Restrict the segmented control to a subset of modes (default: all five). */
  enabled?: ViewMode[];
}

export interface PageSearchInput {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export interface PageFiltersInput {
  /**
   * When false (or when no page has published), LeftNav renders only the
   * brand + admin link + a disabled filter input. This prevents stale facets
   * from a previous route leaking into the next.
   */
  enabled: boolean;
  facetGroups?: PageFacetGroup[];
  viewMode?: PageViewModeInput;
  search?: PageSearchInput;
}

export interface AssistantEntity {
  /** e.g. 'policy' | 'building' | 'finding' | 'inspection' | 'claim' | ... */
  type: string;
  id: string;
  /** Shown in the pinned chip above the chat. */
  displayName: string;
  /** Optional canonical route, defaults to the current location. */
  route?: string;
  /** Optional short summary sent to /chat for grounding. */
  summary?: string;
}

export interface AssistantPage {
  route: string;
  title?: string;
}

export interface AssistantContextInput {
  entity?: AssistantEntity;
  page?: AssistantPage;
  /** Per-page prompt suggestions shown in the placeholder. */
  suggestedPrompts?: string[];
}

// ---------------------------------------------------------------------------
// Empty defaults
//
// Frozen so accidental mutation by a consumer can't corrupt every page.
// ---------------------------------------------------------------------------

const EMPTY_FILTERS: PageFiltersInput = Object.freeze({ enabled: false });
const EMPTY_ASSISTANT: AssistantContextInput = Object.freeze({});

/**
 * Shallow-equal comparison used to skip no-op publishes. PageShell forwards
 * the caller-provided `filters` / `assistantContext` props into the publisher
 * hooks as-is; if a page re-renders with an equivalent object literal, we
 * must not call setState (which would ripple through every subscriber and
 * potentially every page beneath PagePanelProvider).
 */
function shallowEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (
    typeof a !== 'object' ||
    typeof b !== 'object' ||
    a === null ||
    b === null
  ) {
    return false;
  }
  const keysA = Object.keys(a as object);
  const keysB = Object.keys(b as object);
  if (keysA.length !== keysB.length) return false;
  for (const k of keysA) {
    if (!Object.prototype.hasOwnProperty.call(b, k)) return false;
    if (!Object.is((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k])) {
      return false;
    }
  }
  return true;
}

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

/**
 * Publisher-id token pattern: every publisher receives a unique id on mount
 * (via usePageFilters / useAssistantContext). A publish is only accepted if
 * the caller owns the most recently minted id. This prevents a slow-unmounting
 * previous page from clobbering a freshly-mounted page's publish.
 */
interface PagePanelInternal {
  filters: PageFiltersInput;
  assistant: AssistantContextInput;
  mintFiltersToken: () => number;
  mintAssistantToken: () => number;
  publishFilters: (token: number, value: PageFiltersInput | null) => void;
  publishAssistant: (token: number, value: AssistantContextInput | null) => void;
}

const PagePanelContext = createContext<PagePanelInternal | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function PagePanelProvider({ children }: { children: ReactNode }) {
  const [filters, setFilters] = useState<PageFiltersInput>(EMPTY_FILTERS);
  const [assistant, setAssistant] = useState<AssistantContextInput>(EMPTY_ASSISTANT);

  const filtersCounter = useRef(0);
  const assistantCounter = useRef(0);
  const activeFiltersToken = useRef<number | null>(null);
  const activeAssistantToken = useRef<number | null>(null);

  const mintFiltersToken = useCallback(() => {
    filtersCounter.current += 1;
    const id = filtersCounter.current;
    activeFiltersToken.current = id;
    return id;
  }, []);

  const mintAssistantToken = useCallback(() => {
    assistantCounter.current += 1;
    const id = assistantCounter.current;
    activeAssistantToken.current = id;
    return id;
  }, []);

  const publishFilters = useCallback((token: number, value: PageFiltersInput | null) => {
    if (activeFiltersToken.current !== token) return;
    const next = value ?? EMPTY_FILTERS;
    // Shallow-equal bail-out: PageShell re-invokes the publisher with a fresh
    // object literal every render. Skip identical publishes so subscribers
    // don't re-render unnecessarily.
    setFilters((prev) => (shallowEqual(prev, next) ? prev : next));
  }, []);

  const publishAssistant = useCallback((token: number, value: AssistantContextInput | null) => {
    if (activeAssistantToken.current !== token) return;
    const next = value ?? EMPTY_ASSISTANT;
    setAssistant((prev) => (shallowEqual(prev, next) ? prev : next));
  }, []);

  const value = useMemo<PagePanelInternal>(
    () => ({
      filters,
      assistant,
      mintFiltersToken,
      mintAssistantToken,
      publishFilters,
      publishAssistant,
    }),
    [filters, assistant, mintFiltersToken, mintAssistantToken, publishFilters, publishAssistant],
  );

  return <PagePanelContext.Provider value={value}>{children}</PagePanelContext.Provider>;
}

// ---------------------------------------------------------------------------
// Internal accessor used by publisher hooks
//
// Returns `null` when the provider is not mounted. Publisher hooks treat that
// as a no-op so that page unit tests rendering a PageShell in isolation don't
// need to wrap in <PagePanelProvider>. The production app always mounts the
// provider (see index.tsx) so real users always publish through it.
// ---------------------------------------------------------------------------

export function useInternalPagePanelContext(): PagePanelInternal | null {
  return useContext(PagePanelContext);
}

// ---------------------------------------------------------------------------
// Subscriber hooks (consumed by LeftNav / RightTray)
// ---------------------------------------------------------------------------

export function useLeftNavFilters(): PageFiltersInput {
  const ctx = useContext(PagePanelContext);
  return ctx?.filters ?? EMPTY_FILTERS;
}

export function useAssistantPageContext(): AssistantContextInput {
  const ctx = useContext(PagePanelContext);
  return ctx?.assistant ?? EMPTY_ASSISTANT;
}
